<template>
	<div class="counter-settings">
		<AppCard class="card" :title="getString('counter_ui')" :button-text="getString('save')" @button-click="saveCounterUISettings">
			<template v-slot:body>
				<AppList :items="itemsUI">
					<template v-slot:dropdown-counter="{item}">
						<div class="list-item-header">
							<p class="list-item-title">{{ getString('quantity') }}</p>
							<AppNumberInput ref="most-sold-quantity" :min="4" :max="10" :value="mostSoldQuantity" @changed-value="changeMostSoldQuantity" />
						</div>
					</template>
					<template #customize-buttons-dropdown="{item}">
						<div class="row centered">
							<CounterCustomizeButtons />
						</div>
					</template>
					<template #shortcut-values-dropdown="{item}">
						<AppSortableList ref="shortcut-item-list" :items="shortcuts"  key-prop="business_shortcut_id" @sorted="updateShortcutsOrder" @add-item="addShortcut">
							<template v-slot:dropdown-slot="{item}">
								<div class="dropdown-content">
									<AppNumberInput :ref="item.business_shortcut_id+'input-ref'" :value="item.inputValue" :label="getString('amount_x', [currencyStore.currCurrency.symbol])" :min="0" double-decimal @changed-value="(val) => updateShortcutValue(item.business_shortcut_id, val)"/>
									<div class="item-delete">
										<AppClickableIcon :auto-set-active="false" class="delete-button" icon-name="iconBin" @click="deleteShortcut(item.business_shortcut_id)"/>
									</div>
								</div>
							</template>
						</AppSortableList>
					</template>
					<template #other-payments>
						<AppList :items="otherPayments"/>
					</template>
					<template v-slot:daily-report-times-dropdown="{item}">
						<div class="list-item-header">
							<div class="col-12">
								<div class="row">
									<TimesSlotsSlider editable ref="daily-report-time-slots"/>
								</div>
							</div>
						</div>
					</template>
				</AppList>
			</template>
		</AppCard>
		<AppCard class="card card-margin" :title="getString('payment_interface')" :button-text="getString('save')" @button-click="savePaymentInterfaceSettings">
			<template v-slot:body>
				<AppList :items="itemsPaymentInterface">
					<template #customize-buttons-dropdown="{item}">
						<div class="row centered">
							<CounterCustomizeButtons />
						</div>
					</template>
					<template #shortcut-values-dropdown="{item}">
						<AppSortableList ref="shortcut-item-list-payment" :items="shortcuts"  key-prop="business_shortcut_id" @sorted="updateShortcutsOrder" @add-item="addShortcut">
							<template v-slot:dropdown-slot="{item}">
								<div class="dropdown-content">
									<AppNumberInput :ref="item.business_shortcut_id+'input-ref'" :value="item.inputValue" :label="getString('amount_x', [currencyStore.currCurrency.symbol])" :min="0" double-decimal @changed-value="(val) => updateShortcutValue(item.business_shortcut_id, val)"/>
									<div class="item-delete">
										<AppClickableIcon :auto-set-active="false" class="delete-button" icon-name="iconBin" @click="deleteShortcut(item.business_shortcut_id)"/>
									</div>
								</div>
							</template>
						</AppSortableList>
					</template>
					<template #other-payments>
						<AppList :items="otherPayments"/>
					</template>
					<template #payment-split-dropdown="{item}">
						<AppList :items="paymentSplitItems" />
					</template>
				</AppList>
			</template>
		</AppCard>
		<AppCard class="card card-margin" :title="getString('counter_ops')" :button-text="getString('save')" @button-click="saveCounterOpsSettings">
			<template v-slot:body>
				<AppList :items="itemsOps">
					<template #manual-code-dropdown="{item}">
						<div class="row space-between manual-row-container">
							<p>{{ getString('manual_code_explain') }}</p>
							<div class="code-container">
								<AppTextInput ref="code-manual" :placeholder="getString('manual_code_max')" :label="getString('code')" type="text" />
								<AppButton :text="getString('save')" @click="saveCode" />
							</div>
						</div>
					</template>
				</AppList>
			</template>
		</AppCard>
	</div>
</template>

<script>
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppCard from "@/shared/components/AppCard.vue";
import AppList from "@/shared/components/AppList.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppButton from "@/shared/components/AppButton.vue";
import {useCounterStore, useMenuStore, useUserStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useNotificationStore} from "@/shared/stores";
import {replaceNonAlphanumeric} from "@/shared/helpers/strings";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import CounterCustomizeButtons from "@/console-new/components/config/settings/counter/CounterCustomizeButtons.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppSortableList from "@/shared/components/AppSortableList.vue";
import API from '@/shared/helpers/API.js';
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppClickableIcon from "@/shared/components/AppClickableIcon.vue";
import TimesSlotsSlider from "@/console-new/components/shared/TimesSlotsSlider.vue";

export default {
	name: "CounterSettingsContainer",
	components: {
		AppClickableIcon,
		AppButtonIcon,
		AppSortableList,
		TimesSlotsSlider,
		AppTextInput, CounterCustomizeButtons, AppChevronIcon, AppNumberInput, AppCard, AppList, AppLoader, AppButton },
	setup() {
		let userStore = useUserStore();
		let notificationStore = useNotificationStore();
		let languageStore = useLanguageStore();
		let counterStore = useCounterStore();
		let menuStore = useMenuStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, userStore, replaceNonAlphanumeric, showNotification: notificationStore.showNotification, counterStore, menuStore, currencyStore, languageStore};
	},
	data() {
		return {
			customizeButtons: false,
			shortcutValues: false,
			activeDailyReportTimes: false,
			dailyReportTimes: null,
			customizeButtonsPayment: false,
			shortcutValuesPayment: false,
			activeOtherPayments: false,
			paymentSplitDropdown: false,
		}
	},
	computed: {
		mostSoldQuantity() {
			return this.userStore.currBusiness && this.userStore.currBusiness.most_sold_quantity ? this.userStore.currBusiness.most_sold_quantity : 6;
		},
		hasProducts() {
			return Object.keys(this.menuStore.items || {}).length > 0;
		},
		itemsUI() {
			let items = [{
				title: this.getString("show_most_sold"),
				explain: this.getString( "show_most_sold_explainer", [this.userStore.menuWord.toLowerCase()] ),
				switch: true,
				active: this.userStore.currBusiness && this.userStore.currBusiness.most_sold_quantity,
				callback: this.toggleShowMostSold,
				dropdownSlot: 'dropdown-counter',
			},{
				title: this.getString("show_counter_numpad"),
				explain: this.hasProducts ? null : this.getString("show_counter_numpad_no_products_explain"),
				switch: true,
				active: this.userStore.currBusiness && this.userStore.currBusiness.show_counter_numpad,
				callback: (v) => {
					this.userStore.currBusiness.show_counter_numpad = v;
				},
			}];
			if(this.userStore.currBusiness?.active_plan !== 'free') {
				items.push({
					title: this.getString("show_items_images"),
					switch: true,
					active: this.userStore.currBusiness && this.userStore.currBusiness.counter_images,
					callback: (v) => {
					this.userStore.currBusiness.counter_images = v;
				},
				});
			}
			items.push({
				title: this.getString("daily_report_times"),
				explain: this.getString( "daily_report_times_explain"),
				chevronItem: true,
				active: this.activeDailyReportTimes,
				callback: this.toggleDailyReportTimes,
				dropdownSlot: 'daily-report-times-dropdown',
			});
			return items;
		},
		itemsPaymentInterface() {
			let items = [{
				title: this.getString("customize_buttons"),
				chevronItem: true,
				dropdownSlot: 'customize-buttons-dropdown',
				active: this.customizeButtonsPayment,
				callback: () => {
					this.customizeButtonsPayment = !this.customizeButtonsPayment;
					this.shortcutValuesPayment = false;
					this.activeOtherPayments = false;
					this.paymentSplitDropdown = false;
				},
			},{
				title: this.getString("shortcut_feature_title"),
				chevronItem: true,
				explain: this.getString("shortcut_feature_explain"),
				dropdownSlot: 'shortcut-values-dropdown',
				active: this.shortcutValuesPayment,
				callback: () => {
					this.shortcutValuesPayment = !this.shortcutValuesPayment;
					this.customizeButtonsPayment = false;
					this.activeOtherPayments = false;
					this.paymentSplitDropdown = false;
				}
			}];
			
			if(this.userStore.currBusiness?.active_plan !== 'free') {
				items.push({
					title: this.getString('other_payments'),
					chevronItem: true,
					explain: this.getString("other_payments_personalization_explain"),
					dropdownSlot: 'other-payments',
					active: this.activeOtherPayments,
					callback: () => {
						this.activeOtherPayments = !this.activeOtherPayments;
						this.customizeButtonsPayment = false;
						this.shortcutValuesPayment = false;
						this.paymentSplitDropdown = false;
					}
				});
			}
			
			items.push({
				title: this.getString("payment_items"),
				explain: this.getString("payment_items_explain"),
				chevronItem: true,
				dropdownSlot: 'payment-split-dropdown',
				active: this.paymentSplitDropdown,
				callback: () => {
					this.paymentSplitDropdown = !this.paymentSplitDropdown;
					this.customizeButtonsPayment = false;
					this.shortcutValuesPayment = false;
					this.activeOtherPayments = false;
				}
			});
			
			return items;
		},
		paymentSplitItems() {
			return [{
				title: this.getString("payment_split_title"),
				explain: this.getString("payment_split_title_explain"),
				textInput: true,
				value: this.userStore.currBusiness && this.userStore.currBusiness.counter_split_title ? this.userStore.currBusiness.counter_split_title : 'Quota',
				callbackText: (v) => {
					this.userStore.currBusiness.counter_split_title = v;
				},
			},{
				title: this.getString("payment_total_title"),
				explain: this.getString("payment_total_title_explain"),
				textInput: true,
				value: this.userStore.currBusiness && this.userStore.currBusiness.counter_total_title ? this.userStore.currBusiness.counter_total_title : '',
				placeholder: this.userStore.currBusiness && this.userStore.currBusiness.type === 'other' ? this.getString("payment_total_title_placeholder_other") : this.getString("payment_total_title_placeholder"),
				callbackText: (v) => {
					//replace all characters to have only letters and numbers and spaces
					this.userStore.currBusiness.counter_total_title = this.replaceNonAlphanumeric(v||'');
				},
			}];
		},
		itemsOps() {
			let returningOptions = [
				{
					title: this.getString("autocomplete_payed_orders"),
					explain: this.getString( "autocomplete_payed_orders_explain"),
					switch: true,
					active: this.userStore.currBusiness && this.userStore.currBusiness.autocomplete_payed_counter,
					callback: (v) => {
						this.userStore.currBusiness.autocomplete_payed_counter = v;
					},
				}
			];
			if(this.userStore?.currSubscription !== 'free') {
				returningOptions.push({
					title: this.getString("counter_courses"),
					explain: this.getString( "counter_courses_explain"),
					switch: true,
					active: this.userStore.currBusiness && this.userStore.currBusiness.counter_courses,
					callback: (v) => {
						this.userStore.currBusiness.counter_courses = v;
					},
				});
			}
			if(this.userStore.currPlans.kitchen_monitor) {
				returningOptions.push({
					title: this.getString("counter_single_item_prepared"),
					explain: this.getString("counter_single_item_prepared_explain"),
					switch: true,
					active: this.userStore.currBusiness && this.userStore.currBusiness.counter_single_item_prepared,
					callback: (v) => {
						this.userStore.currBusiness.counter_single_item_prepared = v;
					},
				});
			}
			returningOptions.push({
				title: this.getString("counter_allow_prepared"),
				explain: this.getString("counter_allow_prepared_explain"),
				switch: true,
				active: this.userStore.currBusiness && this.userStore.currBusiness.counter_allow_prepared,
				callback: (v) => {
					this.userStore.currBusiness.counter_allow_prepared = v;
				}
			});
			returningOptions.push(
				{
					title: this.getString("follow_variations_limits"),
					explain: this.getString("follow_variations_limits_explain"),
					switch: true,
					active: this.userStore?.currBusiness?.counter_variations_limits,
					callback: (v) => {
						this.userStore.currBusiness.counter_variations_limits = v;
					},
				},
				{
					title: this.getString("manual_payment_method"),
					explain: this.getString("manual_payment_method_explain"),
					switch: true,
					active: this.userStore.currBusiness && this.userStore.currBusiness.counter_manual,
					callback: (v) => {
						this.userStore.currBusiness.counter_manual = v;
					},
					dropdownSlot: this.userStore.currBusiness && this.userStore.currBusiness.counter_manual_code ? null : 'manual-code-dropdown',
				},
				{
					title: this.getString("complete_orders_before_closing"),
					explain: this.getString("complete_orders_before_closing_explain"),
					switch: true,
					active: this.userStore.currBusiness && this.userStore.currBusiness.counter_complete_orders_close,
					callback: (v) => {
						this.userStore.currBusiness.counter_complete_orders_close = v;
					}
				}
			);
			if(!["basic", "free"].includes(this.userStore.currSubscription)) {
				returningOptions.push({
					title: this.getString("mandatory_table"),
					explain: this.getString("mandatory_table_explain"),
					switch: true,
					active: this.userStore.currBusiness && this.userStore.currBusiness.counter_mandatory_table,
					callback: (v) => {
						this.userStore.currBusiness.counter_mandatory_table = v;
					}
				});
			}
			return returningOptions;
		},
		shortcuts() {
			let shortcuts = [];
			if(this.counterStore.shortcuts === null) return shortcuts;

			this.counterStore.shortcuts.forEach(shortcut => {
				shortcuts.push({
					business_shortcut_id: shortcut.business_shortcut_id,
					shortcut_value: shortcut.shortcut_value,
					order_number: shortcut.order_number,
					active: shortcut.active,
					collapsedDelayed: shortcut.collapsedDelayed,
					inputLabel: 'test',
					inputValue: this.currencyStore.dbToActualValue(shortcut.shortcut_value),
					title: this.currencyStore.readableCurrencyValue(shortcut.shortcut_value),
					dropdownSlot: 'dropdown-slot',
					type: 'number'
				})
			});
			return shortcuts.sort((a, b) => a.order_number - b.order_number);
		},
		otherPayments() {
			if(!this.userStore.currBusiness){ return []; }

			let paymentMethods = [
				{
					title: this.getString('payment_type_paylater'),
					switch: true,
					active: this.userStore.currBusiness.counter_pay_later,
					callback: active => this.userStore.currBusiness.counter_pay_later = active ? '1' : '0'
				},
				{
					title: this.getString('payment_type_ticket'),
					switch: true,
					active: this.userStore.currBusiness.counter_ticket,
					callback: active => this.userStore.currBusiness.counter_ticket = active ? '1' : '0'
				}
			];
			if(this.userStore.currBusiness.country === 'IT') {
				paymentMethods.push({
					title: this.getString('payment_type_satispay'),
					switch: true,
					active: this.userStore.currBusiness.counter_satispay,
					callback: active => this.userStore.currBusiness.counter_satispay = active ? '1' : '0'
				});
			}
			paymentMethods = paymentMethods.concat([
				{
					title: this.getString('payment_type_wire'),
					switch: true,
					active: this.userStore.currBusiness.counter_wire_transfer,
					callback: active => this.userStore.currBusiness.counter_wire_transfer = active ? '1' : '0'
				},
				{
					title: this.getString('payment_type_check'),
					switch: true,
					active: this.userStore.currBusiness.counter_check,
					callback: active => this.userStore.currBusiness.counter_check = active ? '1' : '0'
				}
			]);

			return paymentMethods;
		}
	},
	methods: {
		toggleDailyReportTimes(active) {
			this.activeDailyReportTimes = active;
		},
		changeMostSoldQuantity(value) {
			if(this.userStore?.currBusiness) {
				this.userStore.currBusiness.most_sold_quantity = value;
			}
		},
		saveCounterUISettings() {
			Promise.all([
				new Promise((resolve) => {
					this.$refs["daily-report-time-slots"].saveTimes(success => resolve(success));
				}),
				new Promise((resolve) => {
					this.userStore.updateBusiness({
						most_sold_quantity: this.userStore.currBusiness.most_sold_quantity,
						show_counter_numpad: this.userStore.currBusiness.show_counter_numpad,
						counter_images: this.userStore.currBusiness.counter_images
					}, success => resolve(success));
				})
			]).then(([slotsSuccess, businessSuccess]) => {
				if(slotsSuccess && businessSuccess){
					this.showNotification('generic-success');
				}else {
					this.showNotification('generic-fail');
				}
			});
		},
		savePaymentInterfaceSettings() {
			let shortcutUnique = {}, duplicatedShortcuts = false;
			for (const shortcutUniqueKey in this.shortcuts) {
				const shortcutUniqueItem = this.shortcuts[shortcutUniqueKey];

				if(!shortcutUnique.hasOwnProperty(shortcutUniqueItem.inputValue)) {
					shortcutUnique[shortcutUniqueItem.inputValue] = 1;
				}else {
					shortcutUnique[shortcutUniqueItem.inputValue]++;
					duplicatedShortcuts = true;
				}
			}

			if(duplicatedShortcuts) {
				this.showNotification('duplicated-shortcuts');
				return;
			}

			Promise.all([
				new Promise((resolve) => {
					this.counterStore.saveShortcutsChanges(this.shortcuts, success => resolve(success));
				}),
				new Promise((resolve) => {
					this.userStore.updateBusiness({
						counter_split_title: this.userStore.currBusiness.counter_split_title,
						counter_total_title: this.userStore.currBusiness.counter_total_title,
						counter_primary: this.userStore.currBusiness.counter_primary,
						counter_hide_secondary: this.userStore.currBusiness.counter_hide_secondary,
						counter_hide_subtotal: this.userStore.currBusiness.counter_hide_subtotal,
						counter_shortcut: this.userStore.currBusiness.counter_shortcut,
						counter_pay_later: this.userStore.currBusiness.counter_pay_later,
						counter_ticket: this.userStore.currBusiness.counter_ticket,
						counter_satispay: this.userStore.currBusiness.counter_satispay,
						counter_wire_transfer: this.userStore.currBusiness.counter_wire_transfer,
						counter_check: this.userStore.currBusiness.counter_check
					}, success => resolve(success));
				})
			]).then(([shortcutsSuccess, businessSuccess]) => {
				if(shortcutsSuccess === true && businessSuccess){
					this.showNotification('generic-success');
				}else if(businessSuccess && shortcutsSuccess === 'too_many_shortcuts') {
					this.showNotification('too-many-shortcuts');
				}else {
					this.showNotification('generic-fail');
				}
			});
		},
		saveCounterOpsSettings() {
			this.userStore.updateBusiness({
				autocomplete_payed_counter: this.userStore.currBusiness.autocomplete_payed_counter,
				counter_courses: this.userStore.currBusiness.counter_courses,
				counter_single_item_prepared: this.userStore.currBusiness.counter_single_item_prepared,
				counter_manual: this.userStore.currBusiness.counter_manual,
				counter_variations_limits: this.userStore.currBusiness.counter_variations_limits,
				counter_complete_orders_close: this.userStore.currBusiness.counter_complete_orders_close,
				counter_mandatory_table: this.userStore.currBusiness.counter_mandatory_table,
				counter_allow_prepared: this.userStore.currBusiness.counter_allow_prepared,
			}, success => {
				this.showNotification(success ? 'generic-success' : 'generic-fail');
				this.counterStore.clearCart();
			});
		},
		toggleShowMostSold() {
			if (this.userStore.currBusiness && this.userStore.currBusiness.most_sold_quantity) {
				this.userStore.currBusiness.most_sold_quantity = null;
			} else {
				this.userStore.currBusiness.most_sold_quantity = 6;
			}
		},
		saveCode() {
			let code = this.$refs["code-manual"].currentValue;
			if(code) {
				code = code.replace(/[^1-9]/g, '');
			}
			this.$refs["code-manual"].currentValue = code;
			if(code.length < 5 || code.length > 8) {
				this.showNotification('manual-code-length');
				return;
			}

			API.init().updateBusinessManualCode(this.userStore.currBusinessId, code, result => {
				if(result && result.result && result.result === "OK") {
					if(result.code) {
						this.userStore.currBusiness.counter_manual_code = result.code;
					} else {
						this.userStore.currBusiness.counter_manual_code = "*****";
					}
					this.showNotification('generic-success');
				} else {
					this.showNotification('generic-fail');
				}
			});
		},
		updateShortcutValue(business_shortcut_id, value) {
			let shortcut =  this.counterStore.shortcuts.find(shortcut => shortcut.business_shortcut_id === business_shortcut_id);

			shortcut.shortcut_value = this.currencyStore.actualToDbValue(value);
			shortcut.active = true;
		},
		updateShortcutsOrder(evt, sortedItems) {
			sortedItems.forEach((shortcut, index) => {
				if(shortcut.order_number !== index) {
					this.counterStore.shortcuts.find(fshortcut => fshortcut.business_shortcut_id === shortcut.business_shortcut_id).order_number = index;
				}
			});
		},
		addShortcut() {
			let orderNumber = 0;

			this.counterStore.shortcuts.forEach(shortcut => {
				if(shortcut.order_number > orderNumber) {
					orderNumber = shortcut.order_number;
				}
			});

			this.counterStore.shortcuts.push({
				business_shortcut_id: "new"+Math.floor(1000 + Math.random() * 9000),
				shortcut_value: this.currencyStore.currMinInteger,
				active: true,
				collapsedDelayed: false,
				order_number: orderNumber+1
			});
		},
		deleteShortcut(business_shortcut_id) {
			let index = this.counterStore.shortcuts.findIndex(item => item.business_shortcut_id === business_shortcut_id);
			if(index !== -1) {
				this.counterStore.shortcuts.splice(index, 1);
			}
		}
	},
	mounted() {
		this.counterStore.loadShortcuts();
	},
	watch: {
		'userStore.currBusinessId': function(newVal, oldVal) {
			if(newVal != null && newVal !== oldVal){
				this.counterStore.loadShortcuts();
			}
		},
	}
}
</script>

<style lang="scss" scoped>
.counter-settings .list-item-header {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
}
.manual-row-container {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	gap: 10px;

	.code-container {
		display: flex;
		align-items: flex-end;
		justify-content: center;
		flex-direction: column;
		margin-left: auto;
		gap: 10px;
	}
}
.list {
	:deep(.list-item .dropdown-container) {
		z-index: unset;
	}
	:deep(.list-item .dropdown-content) {
		margin-top: 0;
		background-color: $alpha_1;
	}
	:deep(.list-item .list-item-title) {
		max-width: calc(100% - 120px);
		@media (max-width: 600px) {
			font-size: 14px;
		}
		@media (max-width: 400px) {
			font-size: 12px;
		}
		@media (max-width: 360px) {
			max-width: 100%;
			.go-to-stripe-btn {
				margin-top: 10px;
			}
		}
	}
	&:deep(.list-item:first-of-type:before),
	&:deep(.list-item:last-of-type:after) {
		display: none;
	}
	:deep(.dropdown-header) {
		@include centered;
		@include noSelect;
		justify-content: space-between;
		cursor: pointer;
		padding: 6px 0;

		&.button-row {
			cursor: default;
			padding: 2px 0;
		}
		@media (max-width: 360px) {
			.go-to-stripe-btn {
				margin-top: 10px;
			}
		}
	}
}
.card:deep(.card-body) {
	padding: 0 !important;
}
.extra-text {
	p {
		font-size: 14px;
		color: #9B9B9B;
	}
}
.card-margin {
	margin-top: var(--main-padding);
}
.dropdown-content {
	@include row;
	justify-content: flex-end;
}
.item-delete {
	width: 20px;
	overflow: hidden;
	display: flex;
	justify-content: center;
	align-items: center;
	margin-left: 8px;
	cursor: pointer;

	img {
		width: 100%;
		padding: 3px;
	}
}
</style>